// Dédup / filtre du contenu « widgets » (documents / liens / événements) agrégé sur plusieurs mails.
// Util PUR, partagé entre l'agrégation CustomView (CustomViewManager) et — à terme — le lecteur thread
// (cf. Doc/AB#1727). Extrait tel quel de CustomViewManager : iso-comportement, aligné
// sur la dérivation front historique (getDocuments/Links/EventsFromMailsList) et AB#1681.

import type {
    AttachmentMetadata,
    LinkedEventDetails,
    LinkObject,
} from "../../managers/mail/types/mail";
import { computeDedupKey } from "../extractLinks/extractLinks.types";

/**
 * Dédup des pièces jointes : par `contentId` si présent, sinon `nom|type`.
 * La TAILLE est volontairement exclue (AB#1681 : elle varie au transfert → faux non-dédoublonnés).
 * Ordre d'entrée préservé, premier vu conservé.
 */
export function dedupAttachments(
    attachments: AttachmentMetadata[],
): AttachmentMetadata[] {
    const byKey = new Map<string, AttachmentMetadata>();
    for (const att of attachments) {
        if (!att) continue;
        const key = att.contentId
            ? `cid:${att.contentId}`
            : `nt:${att.name}|${att.type}`;
        if (!byKey.has(key)) byKey.set(key, att);
    }
    return [...byKey.values()];
}

/**
 * Dédup des liens par URL normalisée (`computeDedupKey`, même normalisation que l'extracteur SDK et le front).
 * Liens sans URL ignorés. Ordre d'entrée préservé, premier vu conservé.
 */
export function dedupLinks(links: LinkObject[]): LinkObject[] {
    const byKey = new Map<string, LinkObject>();
    for (const link of links) {
        if (!link?.url) continue;
        const key = computeDedupKey(link.url);
        if (!byKey.has(key)) byKey.set(key, link);
    }
    return [...byKey.values()];
}

/**
 * Ne garde que les événements non passés. ISO-fonctionnel avec le front (`isEventPast`) : « passé »
 * ⇔ `endDateTime` non-null ET `now > endDateTime`. `endDateTime` absent ⇒ conservé. Pas de repli sur
 * `startDateTime` (parité stricte avec le front).
 * @param now epoch ms — injectable pour les tests (défaut : `Date.now()`).
 */
export function filterFutureEvents(
    events: LinkedEventDetails[],
    now: number = Date.now(),
): LinkedEventDetails[] {
    return events.filter((ev) => {
        if (!ev) return false;
        const end = ev.endDateTime;
        if (end === null || end === undefined) return true;
        const t = typeof end === "number" ? end : Date.parse(String(end));
        return Number.isNaN(t) ? true : t >= now;
    });
}
